/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import Popover from '../../common/components/Popover';
import { getIsCurrentDay } from './helpers';

const CalendarDay = ({
  day,
  currentDate,
  clinicalEvents,
  onClinicalEventClick,
}) => {
  const {
    i18n: { language },
  } = useTranslation();

  const appointmentsCount = useMemo(
    () =>
      (
        clinicalEvents.appointments?.filter(
          (event) =>
            event.start.getMonth() === currentDate.getMonth() &&
            event.start.getDate() === day,
        ) || []
      ).length,
    [clinicalEvents, currentDate, day],
  );

  const medicationsCount = useMemo(
    () =>
      (
        clinicalEvents.medications?.filter(
          (event) =>
            event.start.getMonth() === currentDate.getMonth() &&
            event.start.getDate() === day,
        ) || []
      ).length,
    [clinicalEvents, currentDate, day],
  );

  const proceduresCount = useMemo(
    () =>
      (
        clinicalEvents.procedures?.filter(
          (event) =>
            event.start.getMonth() === currentDate.getMonth() &&
            event.start.getDate() === day,
        ) || []
      ).length,
    [clinicalEvents, currentDate, day],
  );

  const totalCount = useMemo(
    () => appointmentsCount + medicationsCount + proceduresCount,
    [appointmentsCount, medicationsCount, proceduresCount],
  );

  const dateFromDay = useMemo(() => {
    const date = new Date(currentDate);
    date.setDate(day);
    return date;
  }, [currentDate, day]);

  return (
    <div
      key={day}
      className="z-calendar__day border-b p-2 text-xs cluster-2 justify-between"
      style={
        getIsCurrentDay(currentDate, day)
          ? { backgroundColor: '#3b82f608' }
          : {}
      }
    >
      <span
        className={`${
          getIsCurrentDay(currentDate, day)
            ? 'border w-5 h-5 flex justify-center items-center rounded-full'
            : ''
        }`}
      >
        {day}
      </span>
      {totalCount > 0 && (
        <Popover
          title={toIntlDateTimeFormat(language, dateFromDay, {
            dateStyle: 'long',
          })}
          content={
            <ul>
              {appointmentsCount > 0 && (
                <li className="italic">
                  <button
                    type="button"
                    className="underline hover:no-underline"
                    onClick={() =>
                      onClinicalEventClick('appointments', dateFromDay)
                    }
                  >
                    {appointmentsCount} appointment
                  </button>
                </li>
              )}
              {medicationsCount > 0 && (
                <li>
                  <button
                    type="button"
                    className="underline hover:no-underline"
                    onClick={() =>
                      onClinicalEventClick('medications', dateFromDay)
                    }
                  >
                    {medicationsCount} medications
                  </button>
                </li>
              )}
              {proceduresCount > 0 && (
                <li>
                  <button
                    type="button"
                    className="underline hover:no-underline"
                    onClick={() =>
                      onClinicalEventClick('procedures', dateFromDay)
                    }
                  >
                    {proceduresCount} procedures
                  </button>
                </li>
              )}
            </ul>
          }
        >
          <span className="underline italic cursor-default select-none">
            +{totalCount}
          </span>
        </Popover>
      )}
    </div>
  );
};

CalendarDay.propTypes = {
  day: PropTypes.number.isRequired,
  currentDate: PropTypes.instanceOf(Date).isRequired,
  clinicalEvents: PropTypes.shape({
    appointments: PropTypes.array,
    medications: PropTypes.array,
    procedures: PropTypes.array,
  }),
  onClinicalEventClick: PropTypes.func,
};

CalendarDay.defaultProps = {
  clinicalEvents: {},
  onClinicalEventClick: () => {},
};

export default CalendarDay;
